// ====================================================== //
// AboutPage.js                                           //
// ====================================================== //
import React, { useEffect, useState } from "react";
import "./AboutPage.css";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { API_BASE } from "../config";
import heroImg from "../assets/images/qodes_image.jpg";
import secondHeroImg from "../assets/images/about-second-section.png";

/* ---- timeline data (unchanged) ----------------------- */
const timeline = [
  {
    year: "2014",
    title: "Qodes is founded within Q-Group",
    text:
      "Born from Q-Group’s vision of creating a strong specialist company in software development. From day one the focus was on quality, expertise and long- term customer relationships.",
  },
  {
    year: "2015",
    title: "First major customer breakthrough",
    text:
      "Secured key consulting assignments in areas such as the automotive industry (and other relevant sectors) — customers who are still with us today. Began to build a reputation as “the small firm with razor - sharp consultants.”",
  },
  {
    year: "2018",
    title: "Established as a specialist consulting company",
    text:
      "Clear positioning in embedded/testing and/or full-stack development. Growing demand for senior profiles.",
  },
  {
    year: "2021",
    title: "A new phase: growth and renewal",
    text:
      "Rebecka is recruited as co-pilot with the mission to scale the company. Work begins on culture, strategy and recruitment for the next level.",
  },
  {
    year: "2022 – 2024",
    title: "Revenue doubles",
    text:
      "Organic growth while continuing to prioritize quality over quantity. Key hires strengthen delivery capability and leadership. Qodes grows while preserving its culture.",
  },
  {
    year: "April 1st 2025 ",
    title: "A new CEO takes the helm",
    text:
      "Rebecka steps in as CEO of Qodes, focusing on scaling up while keeping craftsmanship at heart. The journey toward the consulting firm of the future begins. Gusti steps in as co- pilot for Qodes.",
  },
  {
    year: "2025 and beyond",
    title: "The next chapter",
    text:
      "Strategic goals: sustainable growth, cutting-edge expertise, and proud employees. Investments in skills development, partnerships and innovation.",
  },
];

/* ---- reveal-on-scroll hook (unchanged) --------------- */
const useRevealOnScroll = () => {
  useEffect(() => {
    const io = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("reveal");
            obs.unobserve(e.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    document.querySelectorAll(".reveal-on-scroll").forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
};

/* ---------- compute CSS styles from crop JSON --------- */
function getEmployeeCropStyles(imageJson, presetKey = "employeeCard") {
  if (!imageJson || typeof imageJson !== "object") return null;
  const crop = imageJson?.crops?.[presetKey];
  if (!crop || crop.shape !== "rect" || !crop.box) return null;

  const aspect = Number(crop.aspect) || 1; // width/height (portrait ~0.7142857)
  const { x, y, width, height } = crop.box;
  if (
    typeof x !== "number" ||
    typeof y !== "number" ||
    typeof width !== "number" ||
    typeof height !== "number" ||
    width <= 0 ||
    height <= 0
  ) {
    return null;
  }

  const imgWidthPct = (1 / width) * 100;
  const leftPct = (x / width) * 100;
  const topPct = (y / height) * 100;

  const container = {
    position: "relative",
    width: "100%",
    aspectRatio: String(aspect),
    overflow: "hidden",
    borderRadius: "12px",
    background: "#f3f3f3",
  };

  const img = {
    position: "absolute",
    display: "block",
    width: `${imgWidthPct}%`,
    height: "auto",
    left: `-${leftPct}%`,
    top: `-${topPct}%`,
    userSelect: "none",
    pointerEvents: "none",
  };

  return { container, img };
}

const AboutPage = () => {
  useRevealOnScroll();

  const [crew, setCrew] = useState([]);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    setIsMobile(window.innerWidth <= 768);
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    fetch(`${API_BASE}/employees`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.statusText)))
      .then(setCrew)
      .catch(console.error);
  }, []);

  useEffect(() => {
    if (crew.length === 0) return;
    const io = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("reveal");
            obs.unobserve(e.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    document.querySelectorAll(".reveal-on-scroll").forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [crew]);

  return (
    <div className="about-wrapper">
      <Header />

      {/* ---------------- HERO ---------------- */}
      <section className="about-hero-wrapper">
        <style jsx>{`
          .about-hero::before { background-image: url(${heroImg}); }
        `}</style>
        <div className="about-hero__overlay" />
        <div className="about-hero" style={{ backgroundImage: `url(${heroImg})` }}>
          <div className="page-container">
            <div className="about-hero__content">
              <h1 className="about-title">About Qodes</h1>
              <p className="about-subtitle">
                We are a team of passionate software consultants dedicated to delivering exceptional solutions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- INTRO ---------------- */}
      <section className="about-intro">
        <div className="page-container">
          <div className="reveal-on-scroll">
            <p className="about-body-text">
              Qodes is a Swedish software consultancy firm and part of the Qgroup group.
            </p>
            <p className="reveal-on-scroll about-body-text">
              We specialize in Embedded Software and Software Testing, helping companies strengthen their R&D and IoT organizations with senior engineers and technical specialists.
            </p>
            <p className="reveal-on-scroll about-body-text">
              For us, embedded development covers the full software stack from low-level development close to hardware, FPGA and kernel-related work, to middleware, platform development and modern application and feature development in technologies such as C++, Go/Rust and cloud-connected systems.
            </p>
            <p className="reveal-on-scroll about-body-text">
              Our testing organization works in the same way. We provide consultants within everything from embedded and integration testing close to hardware and software platforms, to QA, automation and application-level testing.
            </p>
            <p className="reveal-on-scroll about-body-text">
              Through QoastbyQodes, we also provide senior backend and software developers within areas such as .NET, Java and Android development.
            </p>
            <p className="reveal-on-scroll about-body-text">
              Many of our consultants work in technically advanced environments within industries such as automotive, electrification, telecom and connected products, where scalability, reliability and software quality are business critical.
            </p>
            <p className="reveal-on-scroll about-body-text">
              What defines Qodes is our focus on seniority, technical excellence and people who truly understand engineering. We believe the best consultants are the ones who combine deep technical expertise with communication, ownership and the ability to create value quickly in complex organizations.
            </p>
          </div>
        </div>
      </section>

      {/* ---------- TIMELINE ---------- */}
      <section className="timeline-section">
        <div className="page-container">
          <h2 className="reveal-on-scroll timeline-title">Our journey so far</h2>
          <div className="timeline">
            {timeline.map((item, idx) => (
              <div
                key={item.year}
                className={`timeline-item ${idx % 2 ? "right" : "left"} reveal-on-scroll`}
              >
                <span className="timeline-year">{item.year}</span>
                <div className="timeline-box">
                  <h3 className="timeline-box-title">{item.title}</h3>
                  <p className="timeline-box-text">{item.text}</p>
                </div>
              </div>
            ))}
            {!isMobile && <div className="timeline-line" />}
          </div>
        </div>
      </section>

      {/* ---------- CREW ---------- */}
      <section className="crew-section">
        <div className="crew-banner" style={{ backgroundImage: `url(${secondHeroImg})` }}>
          <style jsx>{`
            .crew-banner::before { background-image: url(${heroImg}); }
          `}</style>
          <div className="banner-overlay" />
          <div className="page-container">
            <p className="reveal-on-scroll about-title">Meet the crew</p>
          </div>
        </div>

        <div className="page-container">
          <div className="crew-grid">
            {crew.map((c) => {
              const styles = getEmployeeCropStyles(c.image_json, "employeeCard");
              const hasCrop = !!styles;
              return (
                <div key={c.employee_id} className="crew-card reveal-on-scroll">
                  <div
                    className={`crew-photo ${hasCrop ? "has-crop" : "no-crop"}`}
                    style={
                      styles?.container || {
                        position: "relative",
                        width: "100%",
                        aspectRatio: "0.7142857143",
                        overflow: "hidden",
                        borderRadius: "12px",
                        background: "#f3f3f3",
                      }
                    }
                  >
                    <img
                      src={c.image_path}
                      alt={c.name}
                      className={`crew-photo__img ${hasCrop ? "has-crop" : "no-crop"}`}
                      style={styles?.img}
                    />
                  </div>

                  <div className="crew-info">
                    <p className="crew-name">{c.name}</p>
                    <p className="crew-role">{c.role}</p>
                    <p className="crew-branch">{c.branch}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
